/**
 * Web Search Configuration Forms
 * Provider-specific configuration forms for agent web search
 */

import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import type {
  TavilySearchConfig,
  BingSearchConfig,
  GoogleSearchConfig,
  SerpApiSearchConfig,
  BraveSearchConfig,
} from '@/types/webSearch.types';

/**
 * Admin checkbox controlling the seed value for the per-user web-search
 * chat toggle. Provider-agnostic — sits above the provider-specific block
 * in the agent config editor.
 *
 * Initial value semantics:
 *   defaultEnabled === undefined → rendered checked (matches the hook's
 *     resolution rule: undefined behaves like true for backward compat).
 *   defaultEnabled === true      → checked.
 *   defaultEnabled === false     → unchecked.
 *
 * Click always emits an explicit boolean — the parent will never receive
 * `undefined` from a user interaction.
 */
export function WebSearchDefaultStateField({
  defaultEnabled,
  onChange,
}: {
  defaultEnabled: boolean | undefined;
  onChange: (defaultEnabled: boolean) => void;
}) {
  const checked = defaultEnabled !== false;
  return (
    <div>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
        />
        <span className="text-sm text-text-primary">
          <span className="font-medium">
            Default web search ON for new chats
          </span>
          <span className="block text-xs text-text-secondary">
            Users can still toggle this per-conversation in the chat input.
          </span>
        </span>
      </label>
    </div>
  );
}

// Tavily Configuration Form
export function TavilyConfigForm({
  config,
  onChange,
}: {
  config: TavilySearchConfig;
  onChange: (updates: Partial<TavilySearchConfig>) => void;
}) {
  const [showApiKey, setShowApiKey] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const testConnection = async () => {
    if (!config.apiKey) {
      setTestResult({
        success: false,
        message: 'Please enter an API key first',
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch('https://api.tavily.com/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: config.apiKey,
          query: 'test connection',
          max_results: 1,
        }),
      });

      if (response.ok) {
        setTestResult({
          success: true,
          message: 'Connection successful! Your Tavily API key is valid.',
        });
      } else {
        const errorData = await response.json().catch(() => null);
        const errorMessage = errorData?.error || response.statusText;
        setTestResult({
          success: false,
          message: `Connection failed (${response.status}): ${errorMessage}`,
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border-2 border-stellar-primary-light bg-bg-tertiary p-4">
      <h4 className="text-sm font-semibold text-text-primary">Tavily Configuration</h4>

      {/* API Key */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          API Key <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showApiKey ? 'text' : 'password'}
            value={config.apiKey || ''}
            onChange={(e) => onChange({ apiKey: e.target.value })}
            placeholder="tvly-xxxxxxxxxxxxx"
            className="w-full rounded-lg border border-border-medium px-3 py-2 pr-10 text-sm focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <button
            type="button"
            onClick={() => setShowApiKey(!showApiKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
          >
            {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Get your API key from{' '}
          <a
            href="https://tavily.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-stellar-primary hover:underline"
          >
            tavily.com
          </a>
        </p>
      </div>

      {/* Test Connection Button */}
      <div>
        <button
          type="button"
          onClick={testConnection}
          disabled={testing || !config.apiKey}
          className="w-full rounded-lg bg-stellar-primary px-4 py-2 text-sm font-medium text-white hover:bg-stellar-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
        {testResult && (
          <div
            className={`mt-2 rounded-lg p-3 text-xs ${
              testResult.success
                ? 'bg-success-light text-success border border-success'
                : 'bg-error-light text-error border border-error'
            }`}
          >
            {testResult.message}
          </div>
        )}
      </div>

      {/* Search Depth */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">Search Depth</label>
        <select
          value={config.searchDepth || 'basic'}
          onChange={(e) =>
            onChange({ searchDepth: e.target.value as 'basic' | 'advanced' })
          }
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
        >
          <option value="basic">Basic (faster, cheaper)</option>
          <option value="advanced">Advanced (more thorough, costs more)</option>
        </select>
      </div>

      {/* Include AI Answer */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={config.includeAnswer ?? false}
          onChange={(e) => onChange({ includeAnswer: e.target.checked })}
          className="h-4 w-4 rounded border-border-medium text-stellar-primary focus:ring-stellar-primary"
        />
        <span className="text-xs text-text-primary">Include AI-generated answer summary</span>
      </label>

      {/* Domain Filters (Optional) */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Include Domains (comma-separated, optional)
        </label>
        <input
          type="text"
          value={(config.includeDomains || []).join(', ')}
          onChange={(e) =>
            onChange({
              includeDomains: e.target.value
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean),
            })
          }
          placeholder="example.com, docs.microsoft.com"
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1 text-xs text-text-secondary">
          Limit results to specific domains (leave empty for all domains)
        </p>
      </div>
    </div>
  );
}

// Bing Configuration Form
export function BingConfigForm({
  config,
  onChange,
}: {
  config: BingSearchConfig;
  onChange: (updates: Partial<BingSearchConfig>) => void;
}) {
  const [showKey, setShowKey] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const testConnection = async () => {
    if (!config.subscriptionKey) {
      setTestResult({
        success: false,
        message: 'Please enter a subscription key first',
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch(
        'https://api.bing.microsoft.com/v7.0/search?q=test&count=1',
        {
          method: 'GET',
          headers: {
            'Ocp-Apim-Subscription-Key': config.subscriptionKey,
          },
        }
      );

      if (response.ok) {
        setTestResult({
          success: true,
          message: 'Connection successful! Your Bing API key is valid.',
        });
      } else {
        const errorText = await response.text();
        setTestResult({
          success: false,
          message: `Connection failed (${response.status}): ${errorText}`,
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: `Connection error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4 rounded-lg border-2 border-stellar-primary-light bg-bg-tertiary p-4">
      <h4 className="text-sm font-semibold text-text-primary">Bing Search Configuration</h4>

      {/* Subscription Key */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Subscription Key <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={config.subscriptionKey || ''}
            onChange={(e) => onChange({ subscriptionKey: e.target.value })}
            className="w-full rounded-lg border border-border-medium px-3 py-2 pr-10 text-sm focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
          >
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          From Azure Portal → Cognitive Services → Bing Search resource
        </p>
      </div>

      {/* Market */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">Market</label>
        <input
          type="text"
          value={config.market || 'en-US'}
          onChange={(e) => onChange({ market: e.target.value })}
          placeholder="en-US"
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1 text-xs text-text-secondary">Language and region (e.g., en-US, en-GB)</p>
      </div>

      {/* Safe Search */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">Safe Search</label>
        <select
          value={config.safeSearch || 'Moderate'}
          onChange={(e) =>
            onChange({
              safeSearch: e.target.value as 'Off' | 'Moderate' | 'Strict',
            })
          }
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        >
          <option value="Off">Off</option>
          <option value="Moderate">Moderate (recommended)</option>
          <option value="Strict">Strict</option>
        </select>
      </div>

      {/* Test Connection Button */}
      <div>
        <button
          type="button"
          onClick={testConnection}
          disabled={testing || !config.subscriptionKey}
          className="w-full rounded-lg bg-stellar-primary px-4 py-2 text-sm font-medium text-white hover:bg-stellar-primary-dark disabled:bg-bg-tertiary disabled:cursor-not-allowed"
        >
          {testing ? 'Testing...' : 'Test Connection'}
        </button>

        {/* Test Result */}
        {testResult && (
          <div
            className={`mt-2 rounded-lg p-3 text-sm ${
              testResult.success
                ? 'bg-success-light text-success border border-success'
                : 'bg-error-light text-error border border-error'
            }`}
          >
            <p className="font-medium">{testResult.success ? '✓ Success' : '✗ Failed'}</p>
            <p className="mt-1 text-xs">{testResult.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Google Configuration Form
export function GoogleConfigForm({
  config,
  onChange,
}: {
  config: GoogleSearchConfig;
  onChange: (updates: Partial<GoogleSearchConfig>) => void;
}) {
  const [showKey, setShowKey] = React.useState(false);

  return (
    <div className="space-y-4 rounded-lg border-2 border-stellar-primary-light bg-bg-tertiary p-4">
      <h4 className="text-sm font-semibold text-text-primary">Google Custom Search Configuration</h4>

      {/* API Key */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          API Key <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={config.apiKey || ''}
            onChange={(e) => onChange({ apiKey: e.target.value })}
            className="w-full rounded-lg border border-border-medium px-3 py-2 pr-10 text-sm focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
          >
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          From{' '}
          <a
            href="https://console.cloud.google.com/apis/credentials"
            target="_blank"
            rel="noopener noreferrer"
            className="text-stellar-primary hover:underline"
          >
            Google Cloud Console
          </a>
        </p>
      </div>

      {/* Search Engine ID */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Search Engine ID (CX) <span className="text-error">*</span>
        </label>
        <input
          type="text"
          value={config.searchEngineId || ''}
          onChange={(e) => onChange({ searchEngineId: e.target.value })}
          placeholder="0123456789abcdef:xxxxxxxx"
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
          required
        />
        <p className="mt-1 text-xs text-text-secondary">
          From{' '}
          <a
            href="https://programmablesearchengine.google.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-stellar-primary hover:underline"
          >
            Programmable Search Engine
          </a>
        </p>
      </div>

      {/* Date Restrict */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Date Restrict (optional)
        </label>
        <input
          type="text"
          value={config.dateRestrict || ''}
          onChange={(e) => onChange({ dateRestrict: e.target.value })}
          placeholder="d7"
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1 text-xs text-text-secondary">
          e.g., &quot;d7&quot; (last 7 days), &quot;m6&quot; (last 6 months),
          &quot;y1&quot; (last year)
        </p>
      </div>
    </div>
  );
}

// SerpAPI Configuration Form
export function SerpApiConfigForm({
  config,
  onChange,
}: {
  config: SerpApiSearchConfig;
  onChange: (updates: Partial<SerpApiSearchConfig>) => void;
}) {
  const [showKey, setShowKey] = React.useState(false);

  return (
    <div className="space-y-4 rounded-lg border-2 border-stellar-primary-light bg-bg-tertiary p-4">
      <h4 className="text-sm font-semibold text-text-primary">SerpAPI Configuration</h4>

      {/* API Key */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          API Key <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showKey ? 'text' : 'password'}
            value={config.apiKey || ''}
            onChange={(e) => onChange({ apiKey: e.target.value })}
            className="w-full rounded-lg border border-border-medium px-3 py-2 pr-10 text-sm focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <button
            type="button"
            onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
          >
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Get your API key from{' '}
          <a
            href="https://serpapi.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-stellar-primary hover:underline"
          >
            serpapi.com
          </a>
        </p>
      </div>

      {/* Engine Selection */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">Search Engine</label>
        <select
          value={config.engine || 'google'}
          onChange={(e) => onChange({ engine: e.target.value as 'google' | 'bing' })}
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        >
          <option value="google">Google (default)</option>
          <option value="bing">Bing</option>
        </select>
      </div>

      {/* Location */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Location (optional)
        </label>
        <input
          type="text"
          value={config.location || ''}
          onChange={(e) => onChange({ location: e.target.value })}
          placeholder="Austin, Texas, United States"
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1 text-xs text-text-secondary">
          Geo-location for localized results (city, state, country)
        </p>
      </div>
    </div>
  );
}

// Brave Configuration Form
export function BraveConfigForm({
  config,
  onChange,
}: {
  config: BraveSearchConfig;
  onChange: (updates: Partial<BraveSearchConfig>) => void;
}) {
  const [showToken, setShowToken] = React.useState(false);

  return (
    <div className="space-y-4 rounded-lg border-2 border-stellar-primary-light bg-bg-tertiary p-4">
      <h4 className="text-sm font-semibold text-text-primary">Brave Search Configuration</h4>

      {/* Subscription Token */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Subscription Token <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showToken ? 'text' : 'password'}
            value={config.subscriptionToken || ''}
            onChange={(e) => onChange({ subscriptionToken: e.target.value })}
            className="w-full rounded-lg border border-border-medium px-3 py-2 pr-10 text-sm focus:border-stellar-primary focus:outline-none focus:ring-2 focus:ring-stellar-primary focus:ring-opacity-20"
            required
          />
          <button
            type="button"
            onClick={() => setShowToken(!showToken)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
          >
            {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Get your token from{' '}
          <a
            href="https://brave.com/search/api/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-stellar-primary hover:underline"
          >
            Brave Search API
          </a>
        </p>
      </div>

      {/* Country */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">Country</label>
        <input
          type="text"
          value={config.country || 'US'}
          onChange={(e) => onChange({ country: e.target.value })}
          placeholder="US"
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        />
        <p className="mt-1 text-xs text-text-secondary">Two-letter country code (e.g., US, GB, CA)</p>
      </div>

      {/* Freshness */}
      <div>
        <label className="mb-1 block text-xs font-medium text-text-primary">
          Freshness (optional)
        </label>
        <select
          value={config.freshness || ''}
          onChange={(e) => onChange({ freshness: e.target.value || undefined })}
          className="w-full rounded-lg border border-border-medium px-3 py-2 text-sm focus:border-stellar-primary focus:outline-none"
        >
          <option value="">All results</option>
          <option value="pd">Past day</option>
          <option value="pw">Past week</option>
          <option value="pm">Past month</option>
          <option value="py">Past year</option>
        </select>
      </div>
    </div>
  );
}
